import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import colorTheme from "../common/color";

interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  specialty?: string;
  doctor_name_arabic?: string;
  specializations_arabic?: string;
  specializations_english?: string;
  custom_footer?: string;
}

interface PasswordData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    specialty: "",
    doctor_name_arabic: "",
    specializations_arabic: "",
    specializations_english: "",
    custom_footer: "",
  });
  const [passwordData, setPasswordData] = useState<PasswordData>({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [activeTab, setActiveTab] = useState<
    "profile" | "customization" | "password" | "account"
  >("profile");
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Function to fetch fresh user data
  const fetchUserProfile = async () => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL ||
          "https://dr-bassam-elayoubi.caduceuslb.com/api"
        }/profile`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const userData = result.data;

        setProfileData({
          first_name: userData.first_name || "",
          last_name: userData.last_name || "",
          email: userData.email || "",
          phone: userData.phone || "",
          specialty: userData.specialty || "",
          doctor_name_arabic: userData.doctor_name_arabic || "",
          specializations_arabic: userData.specializations_arabic || "",
          specializations_english: userData.specializations_english || "",
          custom_footer: userData.custom_footer || "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
    }
  };

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        phone: user.phone || "",
        specialty: user.specialty || "",
        doctor_name_arabic: user.doctor_name_arabic || "",
        specializations_arabic: user.specializations_arabic || "",
        specializations_english: user.specializations_english || "",
        custom_footer: user.custom_footer || "",
      });
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Fetch fresh data when modal opens
      fetchUserProfile();
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL ||
          "https://dr-bassam-elayoubi.caduceuslb.com/api"
        }/profile`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(profileData),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: "success", text: "Profile updated successfully!" });
        // Fetch fresh user data to display updated values
        await fetchUserProfile();
        // Reload the page after a short delay to update the global user state
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to update profile",
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update profile" });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: "error", text: "New passwords do not match" });
      setLoading(false);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setMessage({
        type: "error",
        text: "Password must be at least 8 characters long",
      });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL ||
          "https://dr-bassam-elayoubi.caduceuslb.com/api"
        }/profile/password`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            current_password: passwordData.current_password,
            new_password: passwordData.new_password,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: "success", text: "Password updated successfully!" });
        setPasswordData({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to update password",
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update password" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveTab("profile");
    setMessage(null);
    setPasswordData({
      current_password: "",
      new_password: "",
      confirm_password: "",
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
        }}
        onClick={handleClose}
        className="settings-modal-backdrop"
      >
        {/* Modal */}
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            width: "100%",
            maxWidth: "700px",
            maxHeight: "90vh",
            overflow: "hidden",
            boxShadow: "0 20px 50px rgba(0, 0, 0, 0.3)",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={(e) => e.stopPropagation()}
          className="settings-modal-content"
        >
          {/* Header */}
          <div
            style={{
              padding: "24px 24px 0 24px",
              borderBottom: `1px solid ${colorTheme.gray200}`,
              flexShrink: 0,
            }}
            className="settings-modal-header"
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
              className="nn"
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: colorTheme.gray900,
                  margin: 0,
                }}
              >
                Settings
              </h2>
              <button
                onClick={handleClose}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: colorTheme.gray500,
                  padding: "4px",
                  borderRadius: "4px",
                  lineHeight: 1,
                }}
                className="settings-close-btn"
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div
              style={{ display: "flex", gap: "0", overflowX: "auto" }}
              className="settings-tabs nn"
            >
              <button
                onClick={() => setActiveTab("profile")}
                style={{
                  padding: "12px 16px",
                  border: "none",
                  background: "transparent",
                  color:
                    activeTab === "profile"
                      ? colorTheme.primary
                      : colorTheme.gray500,
                  borderBottom: `2px solid ${
                    activeTab === "profile" ? colorTheme.primary : "transparent"
                  }`,
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  flex: 1,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
                className="settings-tab-btn"
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab("password")}
                style={{
                  padding: "12px 16px",
                  border: "none",
                  background: "transparent",
                  color:
                    activeTab === "password"
                      ? colorTheme.primary
                      : colorTheme.gray500,
                  borderBottom: `2px solid ${
                    activeTab === "password"
                      ? colorTheme.primary
                      : "transparent"
                  }`,
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  flex: 1,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
                className="settings-tab-btn"
              >
                Password
              </button>
              <button
                onClick={() => setActiveTab("account")}
                style={{
                  padding: "12px 16px",
                  border: "none",
                  background: "transparent",
                  color:
                    activeTab === "account"
                      ? colorTheme.primary
                      : colorTheme.gray500,
                  borderBottom: `2px solid ${
                    activeTab === "account" ? colorTheme.primary : "transparent"
                  }`,
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  flex: 1,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
                className="settings-tab-btn"
              >
                Account
              </button>
            </div>
          </div>

          {/* Content */}
          <div
            style={{
              padding: "24px",
              maxHeight: "60vh",
              overflowY: "auto",
            }}
            className="settings-modal-body"
          >
            {/* Message Display */}
            {message && (
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  background:
                    message.type === "success"
                      ? `${colorTheme.success}15`
                      : `${colorTheme.error}15`,
                  color:
                    message.type === "success"
                      ? colorTheme.success
                      : colorTheme.error,
                  border: `1px solid ${
                    message.type === "success"
                      ? colorTheme.success
                      : colorTheme.error
                  }30`,
                  fontSize: "14px",
                }}
                className="settings-message"
              >
                {message.text}
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === "profile" && (
              <form onSubmit={handleProfileSubmit}>
                <div className="settings-form-grid">
                  <div className="settings-form-group">
                    <label className="settings-label">First Name *</label>
                    <input
                      type="text"
                      required
                      value={profileData.first_name}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          first_name: e.target.value,
                        })
                      }
                      className="settings-input"
                    />
                  </div>
                  <div className="settings-form-group">
                    <label className="settings-label">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={profileData.last_name}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          last_name: e.target.value,
                        })
                      }
                      className="settings-input"
                    />
                  </div>
                </div>

                <div className="settings-form-group">
                  <label className="settings-label">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={profileData.email}
                    onChange={(e) =>
                      setProfileData({ ...profileData, email: e.target.value })
                    }
                    className="settings-input"
                  />
                </div>

                <div className="settings-form-group">
                  <label className="settings-label">Phone Number</label>
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) =>
                      setProfileData({ ...profileData, phone: e.target.value })
                    }
                    className="settings-input"
                  />
                </div>

                {/* Specialty field - only for admins */}
                {user?.roles?.includes("admin") && (
                  <div className="settings-form-group">
                    <label className="settings-label">Specialty</label>
                    <input
                      type="text"
                      value={profileData.specialty}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          specialty: e.target.value,
                        })
                      }
                      className="settings-input"
                    />
                  </div>
                )}

                {/* Print Customization Section - only for doctors (not admins) */}
                {user?.roles?.includes("doctor") &&
                  !user?.roles?.includes("admin") && (
                    <div
                      style={{
                        background: colorTheme.gray50,
                        padding: "16px",
                        borderRadius: "8px",
                        marginBottom: "16px",
                        marginTop: "20px",
                      }}
                    >
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: colorTheme.gray900,
                          margin: "0 0 8px 0",
                        }}
                      >
                        Print Customization
                      </h3>

                      <div className="settings-form-group">
                        <label className="settings-label">
                          Doctor Name (Arabic)
                        </label>
                        <input
                          type="text"
                          value={profileData.doctor_name_arabic}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              doctor_name_arabic: e.target.value,
                            })
                          }
                          className="settings-input"
                          placeholder="الدكتور بسام رهيف الأيوبي"
                          style={{ direction: "rtl", textAlign: "right" }}
                        />
                        {/* <small
                          style={{
                            color: colorTheme.gray500,
                            fontSize: "12px",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          Your specializations in English (use line breaks for
                          multiple specializations)
                        </small> */}
                      </div>
                      <div className="settings-form-group">
                        <label className="settings-label">
                          Specializations (English)
                        </label>
                        <textarea
                          value={profileData.specializations_english}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              specializations_english: e.target.value,
                            })
                          }
                          className="settings-input"
                          placeholder="Vertebro - Neurologue&#10;Médecine Manuelle&#10;Acuponcture - EEG"
                          style={{
                            minHeight: "80px",
                            resize: "vertical",
                          }}
                        />
                        <small
                          style={{
                            color: colorTheme.gray500,
                            fontSize: "12px",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          Your specializations in English (use line breaks for
                          multiple specializations)
                        </small>
                      </div>
                      <div className="settings-form-group">
                        <label className="settings-label">
                          Specializations (Arabic)
                        </label>
                        <textarea
                          value={profileData.specializations_arabic}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              specializations_arabic: e.target.value,
                            })
                          }
                          className="settings-input"
                          placeholder="دماغ وأعصاب - عمود فقري&#10;ديسك تخطيط دماغ"
                          style={{
                            direction: "rtl",
                            textAlign: "right",
                            minHeight: "80px",
                            resize: "vertical",
                          }}
                        />
                        <small
                          style={{
                            color: colorTheme.gray500,
                            fontSize: "12px",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          Your specializations in Arabic (use line breaks for
                          multiple specializations)
                        </small>
                      </div>

                      <div className="settings-form-group">
                        <label className="settings-label">Custom Footer</label>
                        <textarea
                          value={profileData.custom_footer}
                          onChange={(e) =>
                            setProfileData({
                              ...profileData,
                              custom_footer: e.target.value,
                            })
                          }
                          className="settings-input"
                          placeholder="06/413707 طرابلس - البولفار - ركان سنتر - مقابل بنك عودة - طابق 3&#10;بيروت - عائشة بكار 03/608154"
                          style={{
                            minHeight: "100px",
                            resize: "vertical",
                          }}
                        />
                        <small
                          style={{
                            color: colorTheme.gray500,
                            fontSize: "12px",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          Contact information and clinic details for the footer
                          (supports both English and Arabic)
                        </small>
                      </div>
                    </div>
                  )}

                <button
                  type="submit"
                  disabled={loading}
                  className="settings-submit-btn"
                  style={{
                    background: loading
                      ? colorTheme.gray300
                      : colorTheme.primary,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Updating..." : "Update Profile"}
                </button>
              </form>
            )}

            {/* Password Tab */}
            {activeTab === "password" && (
              <form onSubmit={handlePasswordSubmit}>
                <div className="settings-form-group">
                  <label className="settings-label">Current Password *</label>
                  <input
                    type="password"
                    required
                    value={passwordData.current_password}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        current_password: e.target.value,
                      })
                    }
                    className="settings-input"
                  />
                </div>

                <div className="settings-form-group">
                  <label className="settings-label">New Password *</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={passwordData.new_password}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        new_password: e.target.value,
                      })
                    }
                    className="settings-input"
                  />
                  <small
                    style={{
                      color: colorTheme.gray500,
                      fontSize: "12px",
                      display: "block",
                      marginTop: "4px",
                    }}
                  >
                    Must be at least 8 characters long
                  </small>
                </div>

                <div className="settings-form-group">
                  <label className="settings-label">
                    Confirm New Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={passwordData.confirm_password}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        confirm_password: e.target.value,
                      })
                    }
                    className="settings-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="settings-submit-btn"
                  style={{
                    background: loading
                      ? colorTheme.gray300
                      : colorTheme.primary,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </form>
            )}

            {/* Account Tab */}
            {activeTab === "account" && (
              <div style={{ width: "100%" }}>
                {/* User Info Display */}
                <div
                  style={{
                    background: colorTheme.gray50,
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "24px",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: colorTheme.gray900,
                      margin: "0 0 12px 0",
                    }}
                  >
                    Account Information
                  </h3>
                  <div style={{ display: "grid", gap: "8px", width: "100%" }}>
                    <div
                      className="nn"
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          color: colorTheme.gray600,
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        Name:
                      </span>
                      <span
                        style={{
                          color: colorTheme.gray900,
                          fontSize: "14px",
                          fontWeight: "500",
                          flex: 1,
                        }}
                      >
                        {user?.first_name} {user?.last_name}
                      </span>
                    </div>
                    <div
                      className="nn"
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          color: colorTheme.gray600,
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        Email:
                      </span>
                      <span
                        style={{
                          color: colorTheme.gray900,
                          fontSize: "14px",
                          fontWeight: "500",
                          flex: 1,
                          wordBreak: "break-word",
                        }}
                      >
                        {user?.email}
                      </span>
                    </div>
                    <div
                      className="nn"
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          color: colorTheme.gray600,
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        Role:
                      </span>
                      <span
                        style={{
                          color: colorTheme.gray900,
                          fontSize: "14px",
                          fontWeight: "500",
                          flex: 1,
                        }}
                      >
                        {user?.roles?.[0] || user?.role || "User"}
                      </span>
                    </div>
                    {user?.phone && (
                      <div
                        className="nn"
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            color: colorTheme.gray600,
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          Phone:
                        </span>
                        <span
                          style={{
                            color: colorTheme.gray900,
                            fontSize: "14px",
                            fontWeight: "500",
                            flex: 1,
                          }}
                        >
                          {user.phone}
                        </span>
                      </div>
                    )}
                    {user?.specialty && (
                      <div
                        className="nn"
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            color: colorTheme.gray600,
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          Specialty:
                        </span>
                        <span
                          style={{
                            color: colorTheme.gray900,
                            fontSize: "14px",
                            fontWeight: "500",
                            flex: 1,
                          }}
                        >
                          {user.specialty}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Logout Section */}
                <div
                  style={{
                    border: `1px solid ${colorTheme.gray200}`,
                    borderRadius: "8px",
                    padding: "20px",
                    textAlign: "center",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: colorTheme.gray900,
                      margin: "0 0 8px 0",
                    }}
                  >
                    Sign Out
                  </h3>
                  <p
                    style={{
                      color: colorTheme.gray600,
                      fontSize: "14px",
                      margin: "0 0 16px 0",
                    }}
                  >
                    Sign out of your account and return to the login page.
                  </p>
                  <button
                    onClick={() => {
                      handleClose();
                      logout();
                    }}
                    style={{
                      padding: "12px 24px",
                      background: "#ef4444",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#dc2626";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#ef4444";
                    }}
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Styles */}
      <style>
        {`
          .settings-form-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 16px;
          }

          .settings-form-group {
            margin-bottom: 16px;
          }

          .settings-label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: ${colorTheme.gray700};
            font-size: 14px;
          }

          .settings-input {
            width: 100%;
            padding: 12px;
            border: 1px solid ${colorTheme.gray300};
            border-radius: 8px;
            font-size: 16px;
            transition: border-color 0.2s ease;
            box-sizing: border-box;
          }

          .settings-input:focus {
            outline: none;
            border-color: ${colorTheme.primary};
            box-shadow: 0 0 0 3px ${colorTheme.primary}20;
          }

          .settings-submit-btn {
            width: 100%;
            padding: 12px 24px;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 500;
            transition: background-color 0.2s ease;
            margin-top: 8px;
          }

          .settings-close-btn:hover {
            background-color: ${colorTheme.gray100};
          }

          .settings-tab-btn:hover {
            background-color: ${colorTheme.gray50};
          }

          /* Mobile Styles */
          @media (max-width: 768px) {
            .settings-modal-content {
              margin: 8px !important;
              max-width: calc(100% - 16px) !important;
              max-height: calc(100vh - 16px) !important;
              border-radius: 12px !important;
            }

            .settings-modal-header {
              padding: 20px 20px 0 20px !important;
            }

            .settings-modal-header h2 {
              font-size: 20px !important;
            }

            .settings-modal-body {
              padding: 20px !important;
              max-height: calc(100vh - 140px) !important;
            }

            .settings-form-grid {
              grid-template-columns: 1fr !important;
              gap: 12px !important;
            }

            .settings-form-group {
              margin-bottom: 12px !important;
            }

            .settings-input {
              padding: 10px !important;
              font-size: 16px !important;
            }

            .settings-submit-btn {
              font-size: 14px !important;
              padding: 10px 20px !important;
            }

            .settings-tab-btn {
              font-size: 13px !important;
              padding: 10px 8px !important;
            }

            .settings-tabs {
              overflow-x: auto !important;
              -webkit-overflow-scrolling: touch !important;
            }
          }

          /* Desktop Styles - Ensure header is always visible */
          @media (min-width: 769px) {
            .settings-modal-content {
              min-height: 500px;
            }
            
            .settings-modal-header {
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
              position: relative !important;
            }
            
            .settings-modal-body {
              flex: 1;
              overflow-y: auto;
            }
          }

          /* Small Mobile */
          @media (max-width: 480px) {
            .settings-modal-header h2 {
              font-size: 18px !important;
            }

            .settings-close-btn {
              font-size: 20px !important;
            }

            .settings-tab-btn {
              font-size: 12px !important;
              padding: 8px 6px !important;
            }
          }

          /* Tablet Styles */
          @media (min-width: 769px) and (max-width: 1024px) {
            .settings-modal-content {
              max-width: 600px !important;
            }

            .settings-form-grid {
              gap: 14px !important;
            }

            .settings-input {
              font-size: 15px !important;
            }

            .settings-submit-btn {
              font-size: 15px !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default UserSettingsModal;
